import { useCallback } from "react";
import { Document } from "models/schemas";
import { FileMetadata } from "components/input-files/definitions";
import { DownloadUrlRequest } from "../documents-table/definitions";
import {
  getFileExtension,
  getFileNameWithoutExtension,
  getFileSize,
} from "utils/date-format";
import { StagedFile, WizardFileOperationsProps } from "./definitions";
import {
  createDocumentCollection,
  getVersionKey,
  VersionedDocument,
} from "../documents-table/version-utils";
import {
  getAllVersionsForFile,
  createMergedDocumentCollection,
  ensureVersionsInStagedFiles,
  createStagedFileFromDocument,
} from "./version-utils";
import {
  processNewFileUploads,
  processPendingVersions,
  processRenamedFiles,
  processFileDeletions,
} from "./utils";

export const useWizardFileOperations = (props: WizardFileOperationsProps) => {
  const {
    uploadUrlCall,
    registerDocCall,
    deleteDocCall,
    updateDocCall,
    lazyDownloadUrlCall,
    addNotification,
    teamName,
    currentUser,
    parentId,
    category,
    stagedFiles,
    onStagedFilesChange,
    isCreateMode,
    documentCollection,
    onDocumentCollectionChange,
  } = props;

  const downloadSingleDoc = useCallback(
    async (stagedFile: StagedFile) => {
      try {
        if (stagedFile.originalDocument?.s3Prefix) {
          let request: DownloadUrlRequest = {
            documentId: stagedFile.originalDocument.documentId,
            preview: false,
          };
          const response = await lazyDownloadUrlCall(request).unwrap();

          if (response?.url) {
            window.location.href = response.url;
          }
        }
      } catch (e) {
        addNotification({
          type: "error",
          header: `File download failed.`,
          dismissible: true,
          timeout: 5000,
        });
      }
    },
    [lazyDownloadUrlCall, addNotification]
  );

  const downloadDocs = useCallback(
    async (selectedFiles: StagedFile[]) => {
      for (const stagedFile of selectedFiles) {
        if (stagedFile.originalDocument) {
          await downloadSingleDoc(stagedFile);
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }
    },
    [downloadSingleDoc]
  );

  const addStagedFile = useCallback(
    (file: File, fileName: string, documentType: string) => {
      const newStagedFile: StagedFile = {
        id: crypto.randomUUID(),
        file,
        fileName,
        documentType,
        isNew: true,
      };
      onStagedFilesChange([...stagedFiles, newStagedFile]);
    },
    [stagedFiles, onStagedFilesChange]
  );

  const addStagedLink = useCallback(
    (link: string, fileName: string, documentType: string) => {
      const newStagedFile: StagedFile = {
        id: crypto.randomUUID(),
        link,
        fileName,
        documentType,
        isNew: true,
      };
      onStagedFilesChange([...stagedFiles, newStagedFile]);
    },
    [stagedFiles, onStagedFilesChange]
  );

  const addMultipleStagedFiles = useCallback(
    (files: FileMetadata[]) => {
      // Build a merged collection to check for version relationships
      const mergedCollection = createMergedDocumentCollection(
        documentCollection || null,
        stagedFiles
      );

      const updatedStagedFiles = [...stagedFiles];
      const filesToAdd: StagedFile[] = [];

      files.forEach((fileMetadata) => {
        // Create a temp document to check version key
        const extension = getFileExtension(fileMetadata.file.name);
        const tempDoc: Document = {
          documentId: "temp-" + crypto.randomUUID(),
          fileName: fileMetadata.fileName,
          extentionType: extension,
          parentId: parentId || "",
          documentType: fileMetadata.documentType,
          teamName: teamName,
          securityLevel: "",
          deleted: false,
          createdAt: Date.now().toString(),
        };

        const versionKey = getVersionKey(tempDoc);
        const existingGroup =
          mergedCollection?.versionGroupsMap.get(versionKey);

        if (existingGroup) {
          // Look for the staged file by matching the version key
          const existingStagedFileIndex = updatedStagedFiles.findIndex((sf) => {
            if (sf.originalDocument) {
              const doc = { ...sf.originalDocument, fileName: sf.fileName };
              return getVersionKey(doc) === versionKey;
            } else if (sf.file) {
              const sfExtension = getFileExtension(sf.file.name);
              const sfDoc: Document = {
                documentId: `staged-${sf.id}`,
                fileName: sf.fileName,
                extentionType: sfExtension,
                parentId: parentId || "",
                documentType: sf.documentType,
                teamName: teamName,
                securityLevel: "",
                deleted: false,
                createdAt: Date.now().toString(),
              };
              return getVersionKey(sfDoc) === versionKey;
            }
            return false;
          });

          if (existingStagedFileIndex !== -1) {
            // Store the versioned file on the existing staged file (not as separate entry)
            const existingStagedFile =
              updatedStagedFiles[existingStagedFileIndex];
            const currentPendingVersions =
              existingStagedFile.pendingVersions || [];
            const currentTotalVersions =
              existingStagedFile.totalVersions || existingGroup.versionCount;

            updatedStagedFiles[existingStagedFileIndex] = {
              ...existingStagedFile,
              totalVersions: currentTotalVersions + 1,
              pendingVersions: [
                ...currentPendingVersions,
                {
                  file: fileMetadata.file,
                  fileName: fileMetadata.fileName,
                  documentType: fileMetadata.documentType,
                },
              ],
            };
          }
          // Do NOT add this file to filesToAdd - it's hidden as a pending version
        } else {
          // Completely new file
          filesToAdd.push({
            id: crypto.randomUUID(),
            file: fileMetadata.file,
            fileName: fileMetadata.fileName,
            documentType: fileMetadata.documentType,
            isNew: true,
          });
        }
      });

      onStagedFilesChange([...updatedStagedFiles, ...filesToAdd]);
    },
    [stagedFiles, onStagedFilesChange, documentCollection, parentId, teamName]
  );

  const removeStagedFile = useCallback(
    (fileId: string) => {
      const fileToRemove = stagedFiles.find((f) => f.id === fileId);
      if (fileToRemove && !fileToRemove.isNew) {
        // Mark existing file for deletion instead of removing it
        const updatedFiles = stagedFiles.map((f) =>
          f.id === fileId ? { ...f, toDelete: true } : f
        );
        onStagedFilesChange(updatedFiles);
      } else {
        // Remove new staged file completely
        const updatedFiles = stagedFiles.filter((f) => f.id !== fileId);
        onStagedFilesChange(updatedFiles);
      }
    },
    [stagedFiles, onStagedFilesChange]
  );

  const updateStagedFileName = useCallback(
    (fileId: string, newFileName: string) => {
      const fileToRename = stagedFiles.find((f) => f.id === fileId);
      if (!fileToRename) {
        return;
      }

      const allVersions = getAllVersionsForFile(
        documentCollection ?? null,
        fileToRename
      );

      // Ensure all versions are in the staged files array
      let updatedFiles = ensureVersionsInStagedFiles(stagedFiles, allVersions);

      const versionIds = new Set(allVersions.map((v) => v.documentId));
      updatedFiles = updatedFiles.map((f) => {
        if (
          f.id === fileId ||
          (allVersions.length > 1 && versionIds.has(f.id))
        ) {
          // Also update fileName in pendingVersions if they exist
          const updatedPendingVersions = f.pendingVersions?.map((pv) => ({
            ...pv,
            fileName: newFileName,
          }));

          return {
            ...f,
            fileName: newFileName,
            ...(updatedPendingVersions && {
              pendingVersions: updatedPendingVersions,
            }),
          };
        }
        return f;
      });

      onStagedFilesChange(updatedFiles);
    },
    [stagedFiles, onStagedFilesChange, documentCollection]
  );

  const restoreStagedFile = useCallback(
    (fileId: string) => {
      const updatedFiles = stagedFiles.map((f) =>
        f.id === fileId ? { ...f, toDelete: false } : f
      );
      onStagedFilesChange(updatedFiles);
    },
    [stagedFiles, onStagedFilesChange]
  );

  // Function to process all staged files after successful wizard submission
  const processStagedFiles = useCallback(
    async (actualParentId: string, gaiaObjectIdOverride?: string) => {
      // props.gaiaObjectId is used when there exists a valid Id at the time of mounting useWizardFileOperations
      // Ex: an assessment with a parent noticeId
      // gaiaObjectIdOverride is passed to processStagedFiles directly in cases where useWizardFileOperations is mounted before
      // there is a valid Id. Ex adding files when creating an event
      const effectiveGaiaObjectId = gaiaObjectIdOverride || props.gaiaObjectId;
      try {
        // Process new file uploads
        const uploadResults = await processNewFileUploads(
          stagedFiles,
          actualParentId,
          effectiveGaiaObjectId,
          category,
          teamName,
          uploadUrlCall,
          registerDocCall
        );

        // Process pending versions
        const versionResults = await processPendingVersions(
          stagedFiles,
          actualParentId,
          effectiveGaiaObjectId,
          category,
          teamName,
          uploadUrlCall,
          registerDocCall
        );

        // Process renamed files
        const renameResults = await processRenamedFiles(
          stagedFiles,
          documentCollection || null,
          updateDocCall
        );

        // Process deletions
        const deleteResults = await processFileDeletions(
          stagedFiles,
          deleteDocCall
        );

        // Aggregate results
        const totalUploaded =
          uploadResults.uploaded +
          versionResults.uploaded +
          renameResults.updated;
        const totalErrors =
          uploadResults.errors +
          versionResults.errors +
          renameResults.errors +
          deleteResults.errors;

        // Show notifications
        const newFileCount = stagedFiles.filter(
          (f) => f.isNew && !f.toDelete
        ).length;
        const renamedFileCount = stagedFiles.filter(
          (f) =>
            !f.isNew &&
            !f.toDelete &&
            f.originalDocument &&
            f.fileName !==
              getFileNameWithoutExtension(f.originalDocument.fileName)
        ).length;

        if (newFileCount > 0) {
          addNotification({
            type: "success",
            header: `${newFileCount} file${
              newFileCount > 1 ? "s" : ""
            } uploaded successfully.`,
            dismissible: true,
            timeout: 5000,
          });
        }

        if (renamedFileCount > 0) {
          addNotification({
            type: "success",
            header: `${renamedFileCount} file${
              renamedFileCount > 1 ? "s" : ""
            } renamed successfully.`,
            dismissible: true,
            timeout: 5000,
          });
        }

        if (deleteResults.deleted > 0) {
          addNotification({
            type: "success",
            header: `${deleteResults.deleted} file${
              deleteResults.deleted > 1 ? "s" : ""
            } deleted successfully.`,
            dismissible: true,
            timeout: 5000,
          });
        }

        if (totalErrors > 0) {
          addNotification({
            type: "error",
            header: `${totalErrors} file operation${
              totalErrors > 1 ? "s" : ""
            } failed.`,
            dismissible: true,
            timeout: 5000,
          });
        }

        return {
          uploaded: totalUploaded,
          deleted: deleteResults.deleted,
          errors: totalErrors,
        };
      } catch (e) {
        addNotification({
          type: "error",
          header: "File operations failed.",
          dismissible: true,
          timeout: 5000,
        });
        return { uploaded: 0, deleted: 0, errors: stagedFiles.length };
      }
    },
    [
      stagedFiles,
      uploadUrlCall,
      registerDocCall,
      deleteDocCall,
      updateDocCall,
      teamName,
      category,
      props.gaiaObjectId,
      addNotification,
      documentCollection,
    ]
  );

  // Function to load existing files for edit mode
  const loadExistingFiles = useCallback(
    (documents: Document[]) => {
      const collection = createDocumentCollection(documents);

      const existingFiles: StagedFile[] = collection.displayDocuments.map(
        (doc: VersionedDocument) =>
          createStagedFileFromDocument(doc, {
            versionKey: getVersionKey(doc),
          })
      );

      onStagedFilesChange(existingFiles);
      if (onDocumentCollectionChange) {
        onDocumentCollectionChange(collection);
      }
    },
    [onStagedFilesChange, onDocumentCollectionChange]
  );

  return {
    downloadSingleDoc,
    downloadDocs,
    addStagedFile,
    addStagedLink,
    addMultipleStagedFiles,
    removeStagedFile,
    updateStagedFileName,
    restoreStagedFile,
    processStagedFiles,
    loadExistingFiles,
  };
};
